/**
 * FolderTree Component
 * Hierarchical folder navigation with expand/collapse functionality
 */

import React, { useState, useEffect, useCallback } from 'react';

const TreeNode = ({ folder, level = 0, currentFolderId, onFolderSelect, expandedFolders, onToggleExpand }) => {
  const isExpanded = expandedFolders.has(folder.id);
  const isActive = currentFolderId === folder.id;
  const hasChildren = folder.children && folder.children.length > 0;

  const handleClick = (e) => {
    e.stopPropagation();
    onFolderSelect(folder.id, folder.name);
  };

  const handleToggle = (e) => {
    e.stopPropagation();
    if (hasChildren) {
      onToggleExpand(folder.id);
    }
  };

  return (
    <div className="rf-tree-item">
      <div
        className={`rf-tree-node ${isActive ? 'active' : ''}`}
        onClick={handleClick}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
      >
        <span
          className={`rf-tree-toggle ${isExpanded ? 'expanded' : ''} ${!hasChildren ? 'empty' : ''}`}
          onClick={handleToggle}
        >
          <i className="fas fa-chevron-right"></i>
        </span>
        <i className={`rf-tree-icon fas ${isExpanded ? 'fa-folder-open' : 'fa-folder'}`}></i>
        <span className="rf-tree-label" title={folder.name}>
          {folder.name}
        </span>
      </div>

      {hasChildren && isExpanded && (
        <div className="rf-tree-children">
          {folder.children.map((child) => (
            <TreeNode
              key={child.id}
              folder={child}
              level={level + 1}
              currentFolderId={currentFolderId}
              onFolderSelect={onFolderSelect}
              expandedFolders={expandedFolders}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FolderTree = ({ currentFolderId, onFolderSelect, refreshKey }) => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  const fetchFolderTree = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/folder-tree', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      const data = await response.json();

      if (data.success && data.tree) {
        setTree(data.tree);
        // Auto-expand root folder
        if (data.tree.length > 0) {
          setExpandedFolders(new Set([data.tree[0].id]));
        }
      } else {
        throw new Error(data.error || 'Failed to load folders');
      }
    } catch (err) {
      setError(err.message);
      console.error('FolderTree error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFolderTree();
  }, [fetchFolderTree, refreshKey]);

  const handleToggleExpand = (folderId) => {
    setExpandedFolders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  const handleFolderSelect = (folderId, folderName) => {
    // Auto-expand selected folder
    setExpandedFolders((prev) => new Set([...prev, folderId]));
    onFolderSelect(folderId, folderName);
  };

  if (loading) {
    return (
      <div className="rf-folder-tree">
        <div className="rf-tree-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <span style={{ marginLeft: '8px' }}>Loading folders...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rf-folder-tree">
        <div className="rf-empty-state" style={{ padding: '20px' }}>
          <i className="fas fa-exclamation-circle" style={{ color: 'var(--rf-danger)', fontSize: '2rem' }}></i>
          <p style={{ margin: '10px 0 0', fontSize: '0.875rem', color: 'var(--rf-gray-600)' }}>{error}</p>
          <button
            className="rf-btn rf-btn-primary rf-btn-sm"
            style={{ marginTop: '10px' }}
            onClick={fetchFolderTree}
          >
            <i className="fas fa-redo"></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rf-folder-tree-header">
        <i className="fas fa-sitemap" style={{ color: 'var(--rf-primary)' }}></i>
        <h3 className="rf-folder-tree-title">Folders</h3>
      </div>
      <div className="rf-folder-tree">
        {tree.length === 0 ? (
          <div className="rf-empty-state" style={{ padding: '20px' }}>
            <i className="fas fa-folder-open" style={{ fontSize: '2rem', color: 'var(--rf-gray-400)' }}></i>
            <p style={{ margin: '10px 0 0', fontSize: '0.875rem' }}>No folders available</p>
          </div>
        ) : (
          tree.map((folder) => (
            <TreeNode
              key={folder.id}
              folder={folder}
              currentFolderId={currentFolderId}
              onFolderSelect={handleFolderSelect}
              expandedFolders={expandedFolders}
              onToggleExpand={handleToggleExpand}
            />
          ))
        )}
      </div>
    </>
  );
};

export default FolderTree;
